/**
 * D16-B — canonical STATELESS WebRTC signaling protocol authority.
 *
 * ONE source of the authentication envelope + message schema + correlation rules for BOTH authenticated
 * participants: the Operator (useWebRTC) and the new Supervisor transport. It owns NO socket, NO reconnect
 * timer, NO heartbeat, NO React state, NO RTCPeerConnection, NO teardown, NO UI. Two socket lifecycle
 * OWNERS are expected (operator + supervisor) — they are different authenticated participants, not two
 * protocols. Byte-parity with the live RT-3 envelope (hooks/useWebRTC.js) is proven by the parity tests.
 *
 * NOTE (RT-3 safety): useWebRTC is intentionally NOT refactored to import this module in this slice — its
 * proven connect/auth control flow is untouched. The parity tests prove this module is byte-equivalent, so
 * consolidating useWebRTC onto it is a later runtime-verified Milestone-4 migration, not a blind edit now.
 */
export const AUTHENTICATED_SIGNALING_VERSION = 'AUTHENTICATED_SIGNALING_V1';

export const SIGNAL = Object.freeze({
  AUTH: 'auth', AUTH_OK: 'auth_ok',
  OFFER: 'offer', ANSWER: 'answer', ICE: 'ice-candidate', CALL_END: 'call-end',
  SUPERVISOR_OFFER: 'supervisor-offer', SUPERVISOR_ANSWER: 'supervisor-answer',
  SUPERVISOR_ICE: 'supervisor-ice', SUPERVISOR_END: 'supervisor-end',
});

/** Authenticated WebRTC endpoint (identical shape to useWebRTC). */
export function buildSignalingEndpoint({ wsUrl, businessId, userId }) {
  return `${wsUrl}/api/v1/webrtc/${businessId}/${userId}?auth_protocol=v1`;
}

/** AUTH FRAME — field ORDER matches useWebRTC exactly: type, protocol, token, plane, nonce, installation_id. */
export function buildAuthFrame({ token, plane, nonce, installationId }) {
  return {
    type: SIGNAL.AUTH, protocol: AUTHENTICATED_SIGNALING_VERSION, token, plane, nonce,
    installation_id: installationId,
  };
}

export function makeNonce() {
  return `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

export const isAuthAck = (m) => !!m && m.type === SIGNAL.AUTH_OK;

export function safeParse(data) {
  try { return JSON.parse(data); } catch (_) { return null; }
}

// Primary-call envelope builders — byte-identical field sets to useWebRTC.
export const buildOffer = (offer, target, callId, callType) => ({ type: SIGNAL.OFFER, offer, target, call_type: callType, call_id: callId });
export const buildAnswer = (answer, target, callId) => ({ type: SIGNAL.ANSWER, answer, target, call_id: callId });
export const buildIce = (candidate, target, callId) => ({ type: SIGNAL.ICE, candidate, target, call_id: callId });

// Supervision envelope builders. SUPERVISION_CLIENT_CONTRACT_V1 (FROZEN): the operator receivers on
// Android (SupervisorPeerController.kt) and iOS (supervisorProtocol.ts) REQUIRE the full id set on every
// frame — the offer is DROPPED without `conn_id`, and the operator routes its answer back via
// `manager_user_id`. `meta` carries {conn_id, target_user_id, target_role, manager_user_id,
// primary_call_session_id, mode, audio_direction, tenant_id}. The web operator ignores the extra keys.
export const buildSupervisorOffer = (offer, target, callId, sessionId, meta = {}) => ({ type: SIGNAL.SUPERVISOR_OFFER, offer, target, call_id: callId, supervision_session_id: sessionId, ...meta });
export const buildSupervisorIce = (candidate, target, callId, sessionId, meta = {}) => ({ type: SIGNAL.SUPERVISOR_ICE, candidate, target, call_id: callId, supervision_session_id: sessionId, ...meta });

/** Strict correlation: a received frame must match the active call + supervision session (fail closed). */
export function correlatesTo(msg, { callId, supervisionSessionId } = {}) {
  if (!msg) return false;
  if (callId && msg.call_id && msg.call_id !== callId) return false;
  if (supervisionSessionId && msg.supervision_session_id && msg.supervision_session_id !== supervisionSessionId) return false;
  return true;
}

export const isSupervisionSignal = (m) => !!m && typeof m.type === 'string' && m.type.indexOf('supervisor-') === 0;
